export const baseMessages = {
  en: {
    app: {
      initializing: 'Loading...', // Shown during tokenStore.init() (Keychain read on iOS)
    },
    common: {
      language: "Language",
      save: "Save",
      cancel: "Cancel",
      close: "Close",
      add: "Add",
      edit: "Edit",
      delete: "Delete",
      view: "View",
      search: "Search",
      loading: "Loading...",
      empty: "No data.",
      refresh: "Refresh",
      actions: "Actions",
      selected: "selected",
      yes: "Yes",
      no: "No",
      back: "Back",
      done: "Done",
      create: "Create",
      name: "Name",
      description: "Description",
      saved: "Saved",
      created: "Created",
      deleted: "Deleted",
      error: "Error",
      warning: "Warning",
      confirmDeleteTitle: "Confirm deletion",
      hint: "Hint",
      validation: "Validation",
      saveFailed: "Save failed.",
      deleteFailed: "Delete failed.",
      loadFailed: "Loading failed.",
      copyFailed: "Copy failed.",
      copy: "Copy",
      noPermission: "No permission.",
      active: "Active",
      inactive: "Inactive",
      status: "Status",
      reset: "Reset",
      filterFrom: "From",
      filterTo: "To",
      resetFilters: "Reset filters",
      export: "Export",
      exportPage: "Export (current page)",
      columns: "Columns",
      columnPicker: "Customize columns",
    },
    nav: {
      // Top-level (Drei-Säulen-Modell + Dashboard + Admin)
      dashboards:       "Dashboards",
      dashboard:        "Dashboard",
      overview:         "Overview",
      activity:         "Activities",
      threatNews:       "Threat News",
      threatIntel:      "Threat Intelligence",
      detection:        "Detection",
      customers:        "Customers",
      customerList:     "Customer List",
      admin:            "Administration",

      // Threat News
      newsFeed:         "News Feed",
      ransomwarePosts:  "Ransomware Posts",
      affectedCompanies: "Affected Companies",
      threatMap:        "Threat Map",
      newsSources:      "News Sources",

      // Threat Intelligence
      mitreAttack:      "MITRE ATT&CK",
      threatActors:     "Threat Actors",
      attackerTools:    "Attacker Tools",
      iocs:             "IOCs",
      fileExtensions:   "File Extensions",

      // Detection
      workflows:        "Workflows",
      streams:          "Streams",
      tags:             "Tags",

      // Admin
      users:            "Users",
      companies:        "Companies",
      roles:            "Roles/Policies",
      apiKeys:          "API Keys",
      auditLogs:        "Audit Logs",
      systemStatus:     "System Status",
    },
    overview: {
      kpiLabelTotal:        "Total",
      kpiLabelLast24h:      "Last 24h",
      kpiLabelCountries:    "Countries",
      kpiLabelActiveFeeds:  "Active Feeds",
      kpiLabelFeeds:        "Feeds",
      kpiLabelCritical:     "Critical",
      kpiLabelManaged:      "Managed",
      kpiLabelCommunity:    "Community",
      kpiLabelCompany:      "Company",
      kpiLabelLinks:        "Links",
      kpiLabelUsed:         "Used",
      kpiLabelActive:       "Active",
      kpiLabelRevoked:      "Revoked",
      threatNews: {
        title:        "Threat News — What's happening?",
        description:  "Live picture of the cybersecurity landscape: cyber news from curated RSS sources, current ransomware leak posts, and a geographic threat map. This is your first stop in the morning routine.",
        note:         "Tip: combine ransomware posts with the threat map to spot regional attack waves at a glance.",
        tiles: {
          newsFeed:        "Aggregated cyber news from your RSS sources — latest stories, deduplicated and ranked.",
          ransomwarePosts: "Live feed of ransomware leak posts: which company was hit, by which group, when.",
          threatMap:       "Geographic distribution of recent attacks. Drill down by country and time period.",
          newsSources:     "Manage the RSS feeds that power the news pipeline. Add, disable, or audit sources.",
        },
      },
      threatIntel: {
        title:        "Threat Intelligence — What do we know?",
        description:  "Knowledge base on threat actors, attack techniques, attacker tools and indicators of compromise. The foundation for building targeted detection.",
        note:         "Connect the dots: every MITRE technique links to the actors that use it and the workflows that detect it.",
        tiles: {
          mitreAttack:    "MITRE ATT&CK techniques as the central framework. Browse, search and link to your detections.",
          threatActors:   "Profiles of threat actors and ransomware groups: aliases, tools, victimology, history.",
          attackerTools:  "Tools, malware families and frameworks attackers commonly deploy in the wild.",
          iocs:           "Indicators of compromise (IOCs) — hashes, IPs, domains. Searchable and exportable.",
          fileExtensions: "Ransomware file extensions mapped to threat actor groups. Quick lookup for incident triage.",
        },
      },
      detection: {
        title:        "Detection — How do we protect?",
        description:  "Detection engineering for your Enginsight SIEM: workflows, log streams, and tagging — built directly on top of the Threat Intelligence above.",
        note:         "Tip: every workflow can be linked to MITRE techniques. Mappings drive the future Recommendations engine.",
        tiles: {
          workflows: "Detection workflows with filters, complex conditions and severity. Multi-tenant via visibility settings.",
          streams:   "Log stream definitions — Windows logs, application logs, custom inputs. Connect them to your workflows.",
          tags:      "Cross-cutting tags for workflows and streams. Useful for filtering, reporting and ownership.",
        },
      },
      admin: {
        title:        "Administration — Platform setup",
        description:  "Manage users, organisations, role-based permissions, API keys and audit trails. The control plane of secDASH.",
        note:         "All admin actions are logged. The Audit Logs view is your compliance and incident-investigation entry point.",
        tiles: {
          users:     "User accounts: invite, deactivate, assign roles. Each user belongs to one company.",
          companies: "Tenants on the platform. Toggle Enginsight customer/partner classification per company.",
          roles:     "Role and permission management. Define which users can read, change or admin which areas.",
          apiKeys:      "API keys for machine-to-machine access. Scope-bound, revocable, fully audited.",
          auditLogs:    "Tamper-evident log of all admin and security-relevant actions across the platform.",
          systemStatus: "Live health and version status of all platform services.",
        },
      },
    },
    profile: {
      profile: "Profile",
      settings: "Settings",
      logout: "Logout",
      darkMode: "Dark Mode",
      lightMode: "Light Mode",
    },
    dialogs: {
      unsavedTitle: "Unsaved changes",
      unsavedBody: "You have unsaved changes. Please save or discard them before leaving the page.",
      reviewChanges: "Review changes",
      discardLeave: "Discard and leave",
    },
    dashboard: {
      rename: "Rename",
      deleteDashboard: "Delete dashboard",
      newDashboard: "New dashboard",
      addWidget: "Add widget",
      reset: "Reset",
      empty: "This dashboard is empty.",
      saving: "Saving…",
      // Sections
      addSection: "Add section",
      deleteSection: "Delete section",
      section: "Section",
      sectionTitlePlaceholder: "Section title…",
      sectionColor: "Background color",
      moveUp: "Move up",
      moveDown: "Move down",
      collapse: "Collapse/expand",
      emptySection: "This section is empty.",
      // Widgets
      newWidget: "New widget",
      customWidget: "Custom widget",
      customWidgetDesc: "Empty widget — pick visualization, data source and options yourself.",
      configureWidget: "Configure widget",
      removeWidget: "Remove widget",
      widgetLoadFailed: "Failed to load data.",
      widgetNoData: "No data available.",
      // Configurator
      chooseViz: "Visualization",
      chooseSource: "Data source",
      options: "Options",
      widgetTitle: "Title (optional)",
      dimension: "Dimension",
      field: "Metric",
      fields: "Metrics",
      timeRange: "Time range",
      limit: "Entries",
      customer: "Customer",
      allCustomers: "All customers",
      color: "Color",
      colorAuto: "Automatic",
      moveToSection: "Move to section",
      back: "Back",
      apply: "Apply",
      daysSuffix: "days",
      categories: "Categories",
      sortCategories: "Sort categories",
      cloneWidget: "Duplicate widget",
      areaLabels: {
        name:          "Name",
        description:   "Description",
        severity:      "Severity",
        visibility:    "Visibility",
        tags:          "Tags",
        featured:      "Featured",
        category_id:   "Category",
        language_code: "Language",
      },
    },
    welcomeWidget: {
      tabs: {
        overview:    "Overview",
        threatNews:  "Threat News",
        threatIntel: "Threat Intelligence",
        detection:   "Detection",
      },
      overview: {
        subheadline: "secDASH brings together what's happening in cybersecurity, what we know about it, and how to detect it — in one workflow built for security analysts.",
      },
      pillars: {
        observe: {
          section:  "Observe",
          title:    "Threat News",
          subtitle: "What's happening right now? — News, ransomware leaks, threat map.",
        },
        understand: {
          section:  "Understand",
          title:    "Threat Intelligence",
          subtitle: "What do we know? — Actors, MITRE ATT&CK, tools, IOCs.",
        },
        detect: {
          section:  "Detect",
          title:    "Detection",
          subtitle: "How do we protect? — Workflows, streams, tags for your SIEM.",
        },
      },
      detail: {
        threatNews: {
          title: "Threat News — Observe what's happening",
          body:  "The live picture of cybersecurity. Curated cyber news from RSS sources, ransomware leak posts, and a geographic threat map — your morning routine entry point.",
          bullets: [
            "Aggregated cyber news from your RSS feeds",
            "Live ransomware leak posts: who was hit, by whom, when",
            "Geographic threat map with country drilldown",
            "Manage and audit your news sources",
          ],
        },
        threatIntel: {
          title: "Threat Intelligence — Understand the adversary",
          body:  "The knowledge base behind every detection. MITRE ATT&CK, threat actor profiles, attacker tools and indicators of compromise — fully linked, always searchable.",
          bullets: [
            "MITRE ATT&CK techniques as the central framework",
            "Threat actor profiles with aliases, tools and victimology",
            "Attacker tools and ransomware file extensions",
            "IOCs ready to feed into your detection",
          ],
        },
        detection: {
          title: "Detection — Protect with engineering",
          body:  "Your Enginsight SIEM configuration, built directly on the Threat Intelligence above. Workflows, streams and tags — multi-tenant, with future Recommendations from the community.",
          bullets: [
            "Detection workflows with filters and severity",
            "Log stream definitions linked to your workflows",
            "Cross-cutting tags for filtering and reporting",
            "Soon: AI-powered Recommendations from community data",
          ],
        },
      },
      openSection: "Open section",
      footerHint:  "You can remove this widget at any time via the Edit (pencil) icon — and add it back via 'Add widget' under 'Onboarding'.",
    },
    activity: {
      colTime: "Timestamp",
      colAction: "Action",
      colResource: "Resource",
      colUser: "User",
      empty: "No activities found.",
      pageReport: "{first}–{last} of {totalRecords}",
      actions: {
        'workflow.create':        'Workflow created',
        'workflow.update':        'Workflow updated',
        'workflow.delete':        'Workflow deleted',
        'stream.create':          'Stream created',
        'stream.update':          'Stream updated',
        'stream.delete':          'Stream deleted',
        'tag.create':             'Tag created',
        'tag.update':             'Tag updated',
        'tag.delete':             'Tag deleted',
        'hackwatch.group.create': 'HackWatch group created',
        'hackwatch.group.update': 'HackWatch group updated',
        'hackwatch.group.delete': 'HackWatch group deleted',
        'banner.create':          'Banner created',
        'banner.update':          'Banner updated',
        'banner.delete':          'Banner deleted',
        'user.create':            'User created',
        'user.update':            'User updated',
        'user.delete':            'User deleted',
        'role.create':            'Role created',
        'role.update':            'Role updated',
        'role.delete':            'Role deleted',
        'company.create':         'Company created',
        'company.update':         'Company updated',
        'company.delete':         'Company deleted',
        'api_key.create':         'API key created',
        'api_key.revoke':         'API key revoked',
        'auth.login':             'Login',
      },
    },
    login: {
      email: "Email Address",
      password: "Password",
      rememberMe: "Stay logged in",
      loginBtn: "Login",
      invalidCredentials: "Invalid credentials.",
      loginError: "An error occurred. Please try again later.",
      forgotPassword: "Forgot your password?",
      orDivider: "or",
    },
    auth: {
      backToLogin: "Back to login",
      goToLogin: "Go to login",
      requestNewLink: "Request a new link",
      forgot: {
        title: "Forgot your password?",
        description: "Enter your email address and we'll send you a link to reset your password.",
        submit: "Send reset link",
        successTitle: "Check your inbox",
        successMessage: "If an account exists for this address, we've sent you an email with a reset link. The link is valid for 60 minutes.",
        invalidEmail: "Please enter a valid email address.",
        genericError: "Something went wrong. Please try again later.",
      },
      set: {
        title: "Set a new password",
        description: "Choose a strong password with at least 8 characters.",
        newPassword: "New password",
        confirmPassword: "Confirm password",
        submit: "Save password",
        successTitle: "Password updated",
        successMessage: "Your password has been changed. You'll be redirected to the login page in a moment.",
        passwordTooShort: "Password must be at least 8 characters long.",
        passwordMismatch: "The passwords do not match.",
        tokenMissing: "Reset link is invalid",
        tokenMissingHelp: "The reset link is incomplete. Please request a new one.",
        tokenInvalid: "This reset link is invalid or has expired. Please request a new one.",
        genericError: "Something went wrong. Please try again later.",
      },
      invite: {
        title: "Welcome to SECSU",
        description: "Set your password to activate your account and sign in.",
        submit: "Activate account",
        successTitle: "Account activated",
        successMessage: "Your account is ready. You'll be redirected to the login page in a moment.",
      },
    },
    footer: {
      tagline: "stay secure",
    },
    // ── Phase 4: Biometrics ────────────────────────────────────────────────
    biometric: {
      lockedHint: 'Authenticate to continue.',
      unlockReason: 'Unlock secDASH',
      usePassword: 'Use password instead',
      settingsLabel: 'Unlock with {label}',
      settingsDesc: 'Use Face ID or Touch ID to unlock the app after it has been in the background.',
      confirmEnable: 'Confirm to activate biometric unlock',
      enabledSuccess: '{label} enabled.',
      disabledSuccess: 'Biometric unlock disabled.',
      toggleFailed: 'Could not change biometric setting.',
    },
    // ── WebAuthn / Passkeys ────────────────────────────────────────────────
    passkey: {
      title:           'Passkeys',
      loginBtn:        'Sign in with Face ID / Passkey',
      authError:       'Passkey authentication failed. Please use your password.',
      notSupported:    'This device or browser does not support passkeys.',
      noPasskeys:      'No passkeys registered yet.',
      unnamed:         'Unnamed passkey',
      addBtn:          'Add passkey',
      addBtnConfirm:   'Create passkey',
      addHint:         'Your device will prompt you for Face ID, Touch ID, or Windows Hello.',
      registerSuccess: 'Passkey successfully created.',
      registerError:   'Failed to create passkey. Please try again.',
      deleteConfirm:   'Delete passkey',
      created:         'Created',
      lastUsed:        'Last used',
      backedUp:        'Synced',
      backedUpHint:    'This passkey is synced across your devices (e.g. via iCloud Keychain).',
      namePlaceholder: 'e.g. iPhone 15, MacBook Pro',
    },
  },
  de: {
    app: {
      initializing: 'Wird geladen...', // Spinner während tokenStore.init() (Keychain-Lesen auf iOS)
    },
    common: {
      language: "Sprache",
      save: "Speichern",
      cancel: "Abbrechen",
      close: "Schließen",
      add: "Hinzufügen",
      edit: "Bearbeiten",
      delete: "Löschen",
      view: "Ansehen",
      search: "Suche",
      loading: "Lädt...",
      empty: "Keine Daten.",
      refresh: "Aktualisieren",
      actions: "Aktionen",
      selected: "ausgewählt",
      yes: "Ja",
      no: "Nein",
      back: "Zurück",
      done: "Fertig",
      create: "Erstellen",
      name: "Name",
      description: "Beschreibung",
      saved: "Gespeichert",
      created: "Erstellt",
      deleted: "Gelöscht",
      error: "Fehler",
      warning: "Hinweis",
      confirmDeleteTitle: "Löschen bestätigen",
      hint: "Hinweis",
      validation: "Validierung",
      saveFailed: "Speichern fehlgeschlagen.",
      deleteFailed: "Löschen fehlgeschlagen.",
      loadFailed: "Laden fehlgeschlagen.",
      copyFailed: "Kopieren fehlgeschlagen.",
      copy: "Kopieren",
      noPermission: "Keine Berechtigung.",
      active: "Aktiv",
      inactive: "Inaktiv",
      status: "Status",
      reset: "Zurücksetzen",
      filterFrom: "Von",
      filterTo: "Bis",
      resetFilters: "Filter zurücksetzen",
      export: "Exportieren",
      exportPage: "Exportieren (aktuelle Seite)",
      columns: "Spalten",
      columnPicker: "Spalten anpassen",
    },
    nav: {
      // Top-level (Drei-Säulen-Modell + Dashboard + Admin)
      dashboards:       "Dashboards",
      dashboard:        "Dashboard",
      overview:         "Übersicht",
      activity:         "Aktivitäten",
      threatNews:       "Threat News",
      threatIntel:      "Threat Intelligence",
      detection:        "Detection",
      customers:        "Kunden",
      customerList:     "Kundenliste",
      admin:            "Administration",

      // Threat News
      newsFeed:         "News-Feed",
      ransomwarePosts:  "Ransomware-Posts",
      affectedCompanies: "Betroffene Unternehmen",
      threatMap:        "Lagekarte",
      newsSources:      "Quellen",

      // Threat Intelligence
      mitreAttack:      "MITRE ATT&CK",
      threatActors:     "Threat Actors",
      attackerTools:    "Angreifer-Tools",
      iocs:             "IOCs",
      fileExtensions:   "Datei-Erweiterungen",

      // Detection
      workflows:        "Workflows",
      streams:          "Streams",
      tags:             "Tags",

      // Admin
      users:            "Benutzer",
      companies:        "Unternehmen",
      roles:            "Rollen/Policies",
      apiKeys:          "API Keys",
      auditLogs:        "Audit Logs",
      systemStatus:     "Systemstatus",
    },
    overview: {
      kpiLabelTotal:        "Gesamt",
      kpiLabelLast24h:      "Letzte 24h",
      kpiLabelCountries:    "Länder",
      kpiLabelActiveFeeds:  "Aktive Feeds",
      kpiLabelFeeds:        "Quellen",
      kpiLabelCritical:     "Kritisch",
      kpiLabelManaged:      "Managed",
      kpiLabelCommunity:    "Community",
      kpiLabelCompany:      "Company",
      kpiLabelLinks:        "Verknüpfungen",
      kpiLabelUsed:         "Verwendet",
      kpiLabelActive:       "Aktiv",
      kpiLabelRevoked:      "Widerrufen",
      threatNews: {
        title:        "Threat News — Was ist gerade los?",
        description:  "Lagebild der aktuellen Cybersicherheits-Welt: kuratierte Cyber-News aus RSS-Quellen, Live-Feed der Ransomware-Leaks und eine geografische Lagekarte. Erste Anlaufstelle in deiner Morgenroutine.",
        note:         "Tipp: Ransomware-Posts mit der Lagekarte kombinieren — so siehst du regionale Angriffswellen auf einen Blick.",
        tiles: {
          newsFeed:        "Aggregierte Cyber-News aus deinen RSS-Quellen — aktuelle Meldungen, dedupliziert und sortiert.",
          ransomwarePosts: "Live-Feed der Ransomware-Leaks: welches Unternehmen wurde von welcher Gruppe getroffen, wann.",
          threatMap:       "Geografische Verteilung aktueller Angriffe. Drilldown nach Land und Zeitraum möglich.",
          newsSources:     "Verwaltung der RSS-Feeds, die die News-Pipeline speisen. Quellen hinzufügen, deaktivieren, prüfen.",
        },
      },
      threatIntel: {
        title:        "Threat Intelligence — Was wissen wir?",
        description:  "Wissensbasis zu Angreifern, Angriffstechniken, Angreifer-Tools und Indikatoren. Grundlage für gezielte Detection.",
        note:         "Verknüpfe die Punkte: jede MITRE-Technique zeigt die Akteure, die sie nutzen — und die Workflows, die sie erkennen.",
        tiles: {
          mitreAttack:    "MITRE ATT&CK-Techniken als zentrales Framework. Durchsuchen und mit deinen Detections verknüpfen.",
          threatActors:   "Profile von Threat Actors und Ransomware-Gruppen: Aliase, Tools, Opfer-Branchen, Historie.",
          attackerTools:  "Tools, Malware-Familien und Frameworks, die Angreifer in freier Wildbahn einsetzen.",
          iocs:           "Indicators of Compromise (IOCs) — Hashes, IPs, Domains. Durchsuchbar und exportierbar.",
          fileExtensions: "Ransomware-Datei-Erweiterungen verknüpft mit Threat-Actor-Gruppen. Schnellsuche im Incident-Triage.",
        },
      },
      detection: {
        title:        "Detection — Wie schützen wir uns?",
        description:  "Detection Engineering für dein Enginsight SIEM: Workflows, Log-Streams und Tags — direkt aufbauend auf der Threat Intelligence.",
        note:         "Tipp: jeder Workflow kann mit MITRE-Techniques verknüpft werden. Diese Mappings füttern später die Recommendations-Engine.",
        tiles: {
          workflows: "Detection-Workflows mit Filtern, Komplex-Bedingungen und Schweregrad. Mandantenfähig via Visibility.",
          streams:   "Definitionen von Log-Quellen — Windows-Logs, Application-Logs, eigene Inputs. Verknüpfung zu deinen Workflows.",
          tags:      "Querschnitt-Tags für Workflows und Streams. Nützlich für Filterung, Reporting und Verantwortlichkeit.",
        },
      },
      admin: {
        title:        "Administration — Plattform-Verwaltung",
        description:  "Verwaltung von Benutzern, Mandanten, Rollen, API-Keys und Audit-Trails. Die Steuerebene von secDASH.",
        note:         "Alle Admin-Aktionen werden protokolliert. Audit Logs sind dein Einstiegspunkt für Compliance und Incident-Untersuchungen.",
        tiles: {
          users:     "Benutzerkonten: einladen, deaktivieren, Rollen zuweisen. Jeder Benutzer gehört zu einem Unternehmen.",
          companies: "Mandanten auf der Plattform. Enginsight-Kunde-/Partner-Klassifizierung pro Unternehmen umschalten.",
          roles:     "Rollen- und Berechtigungs-Management. Wer darf welchen Bereich lesen, ändern oder administrieren.",
          apiKeys:      "API-Keys für Maschine-zu-Maschine-Zugriff. Scope-gebunden, widerrufbar, vollständig protokolliert.",
          auditLogs:    "Manipulationssicheres Protokoll aller administrativen und sicherheitsrelevanten Aktionen.",
          systemStatus: "Live-Gesundheits- und Versionsstatus aller Plattform-Services.",
        },
      },
    },
    profile: {
      profile: "Profil",
      settings: "Einstellungen",
      logout: "Logout",
      darkMode: "Dark Mode",
      lightMode: "Light Mode",
    },
    dialogs: {
      unsavedTitle: "Ungespeicherte Aenderungen",
      unsavedBody: "Du hast ungespeicherte Aenderungen. Bitte speichere sie oder verwerfe sie, bevor du die Seite verlaesst.",
      reviewChanges: "Aenderungen pruefen",
      discardLeave: "Verwerfen und verlassen",
    },
    dashboard: {
      rename: "Umbenennen",
      deleteDashboard: "Dashboard löschen",
      newDashboard: "Neues Dashboard erstellen",
      addWidget: "Widget hinzufügen",
      reset: "Zurücksetzen",
      empty: "Dieses Dashboard ist leer.",
      saving: "Speichern…",
      // Rubriken
      addSection: "Rubrik hinzufügen",
      deleteSection: "Rubrik löschen",
      section: "Rubrik",
      sectionTitlePlaceholder: "Rubrik-Titel…",
      sectionColor: "Hintergrundfarbe",
      moveUp: "Nach oben",
      moveDown: "Nach unten",
      collapse: "Ein-/ausklappen",
      emptySection: "Diese Rubrik ist leer.",
      // Widgets
      newWidget: "Neues Widget",
      customWidget: "Eigenes Widget",
      customWidgetDesc: "Leeres Widget — Darstellung, Datenquelle und Optionen selbst wählen.",
      configureWidget: "Widget konfigurieren",
      removeWidget: "Widget entfernen",
      widgetLoadFailed: "Fehler beim Laden der Daten.",
      widgetNoData: "Keine Daten vorhanden.",
      // Configurator
      chooseViz: "Darstellung",
      chooseSource: "Datenquelle",
      options: "Optionen",
      widgetTitle: "Titel (optional)",
      dimension: "Dimension",
      field: "Kennzahl",
      fields: "Kennzahlen",
      timeRange: "Zeitraum",
      limit: "Einträge",
      customer: "Kunde",
      allCustomers: "Alle Kunden",
      color: "Farbe",
      colorAuto: "Automatisch",
      moveToSection: "In Rubrik verschieben",
      back: "Zurück",
      apply: "Übernehmen",
      daysSuffix: "Tage",
      categories: "Kategorien",
      sortCategories: "Kategorien sortieren",
      cloneWidget: "Widget duplizieren",
      areaLabels: {
        name:          "Name",
        description:   "Beschreibung",
        severity:      "Severity",
        visibility:    "Sichtbarkeit",
        tags:          "Tags",
        featured:      "Featured",
        category_id:   "Kategorie",
        language_code: "Sprache",
      },
    },
    welcomeWidget: {
      tabs: {
        overview:    "Übersicht",
        threatNews:  "Threat News",
        threatIntel: "Threat Intelligence",
        detection:   "Detection",
      },
      overview: {
        subheadline: "secDASH bringt zusammen, was in der Cybersecurity gerade passiert, was wir darüber wissen und wie wir es erkennen können — in einem Workflow für Security-Analysten.",
      },
      pillars: {
        observe: {
          section:  "Beobachten",
          title:    "Threat News",
          subtitle: "Was passiert gerade? — News, Ransomware-Leaks, Lagekarte.",
        },
        understand: {
          section:  "Verstehen",
          title:    "Threat Intelligence",
          subtitle: "Was wissen wir? — Akteure, MITRE ATT&CK, Tools, IOCs.",
        },
        detect: {
          section:  "Erkennen",
          title:    "Detection",
          subtitle: "Wie schütze ich mich? — Workflows, Streams, Tags für dein SIEM.",
        },
      },
      detail: {
        threatNews: {
          title: "Threat News — Beobachten was passiert",
          body:  "Das Lagebild der Cybersecurity. Kuratierte Cyber-News aus RSS-Quellen, Ransomware-Leaks und eine geografische Lagekarte — deine Morgenroutine-Anlaufstelle.",
          bullets: [
            "Aggregierte Cyber-News aus deinen RSS-Quellen",
            "Live-Feed der Ransomware-Leaks: wer, von wem, wann",
            "Geografische Lagekarte mit Drilldown nach Land",
            "Quellen verwalten und prüfen",
          ],
        },
        threatIntel: {
          title: "Threat Intelligence — Den Angreifer verstehen",
          body:  "Die Wissensbasis hinter jeder Detection. MITRE ATT&CK, Threat-Actor-Profile, Angreifer-Tools und Indikatoren — vollständig verknüpft und durchsuchbar.",
          bullets: [
            "MITRE ATT&CK-Techniken als zentrales Framework",
            "Threat-Actor-Profile mit Aliasen, Tools, Opfer-Branchen",
            "Angreifer-Tools und Ransomware-Datei-Erweiterungen",
            "IOCs zum direkten Einsatz in der Detection",
          ],
        },
        detection: {
          title: "Detection — Schutz mit Detection Engineering",
          body:  "Deine Enginsight-SIEM-Konfiguration, direkt aufbauend auf der Threat Intelligence. Workflows, Streams und Tags — mandantenfähig, mit künftigen Empfehlungen aus der Community.",
          bullets: [
            "Detection-Workflows mit Filtern und Schweregrad",
            "Log-Streams verknüpft mit deinen Workflows",
            "Querschnitt-Tags für Filterung und Reporting",
            "Bald: KI-gestützte Empfehlungen aus Community-Daten",
          ],
        },
      },
      openSection: "Bereich öffnen",
      footerHint:  'Du kannst dieses Widget jederzeit über das Stift-Symbol (Bearbeiten) entfernen — und über „Widget hinzufügen" unter „Onboarding" wieder einblenden.',
    },
    activity: {
      colTime: "Zeitpunkt",
      colAction: "Aktion",
      colResource: "Ressource",
      colUser: "Benutzer",
      empty: "Keine Aktivitäten vorhanden.",
      pageReport: "{first}–{last} von {totalRecords}",
      actions: {
        'workflow.create':        'Workflow erstellt',
        'workflow.update':        'Workflow aktualisiert',
        'workflow.delete':        'Workflow gelöscht',
        'stream.create':          'Stream erstellt',
        'stream.update':          'Stream aktualisiert',
        'stream.delete':          'Stream gelöscht',
        'tag.create':             'Tag erstellt',
        'tag.update':             'Tag aktualisiert',
        'tag.delete':             'Tag gelöscht',
        'hackwatch.group.create': 'HackWatch-Gruppe erstellt',
        'hackwatch.group.update': 'HackWatch-Gruppe aktualisiert',
        'hackwatch.group.delete': 'HackWatch-Gruppe gelöscht',
        'banner.create':          'Banner erstellt',
        'banner.update':          'Banner aktualisiert',
        'banner.delete':          'Banner gelöscht',
        'user.create':            'Benutzer erstellt',
        'user.update':            'Benutzer aktualisiert',
        'user.delete':            'Benutzer gelöscht',
        'role.create':            'Rolle erstellt',
        'role.update':            'Rolle aktualisiert',
        'role.delete':            'Rolle gelöscht',
        'company.create':         'Unternehmen erstellt',
        'company.update':         'Unternehmen aktualisiert',
        'company.delete':         'Unternehmen gelöscht',
        'api_key.create':         'API Key erstellt',
        'api_key.revoke':         'API Key widerrufen',
        'auth.login':             'Anmeldung',
      },
    },
    login: {
      email: "E-Mail-Adresse",
      password: "Passwort",
      rememberMe: "Angemeldet bleiben",
      loginBtn: "Anmelden",
      invalidCredentials: "Ungültige Anmeldedaten.",
      loginError: "Ein Fehler ist aufgetreten. Bitte versuchen Sie es später erneut.",
      forgotPassword: "Passwort vergessen?",
      orDivider: "oder",
    },
    auth: {
      backToLogin: "Zurück zum Login",
      goToLogin: "Zum Login",
      requestNewLink: "Neuen Link anfordern",
      forgot: {
        title: "Passwort vergessen?",
        description: "Gib deine E-Mail-Adresse ein und wir schicken dir einen Link zum Zurücksetzen deines Passworts.",
        submit: "Reset-Link anfordern",
        successTitle: "Schau in dein Postfach",
        successMessage: "Falls ein Account zu dieser Adresse existiert, haben wir dir eine E-Mail mit einem Reset-Link gesendet. Der Link ist 60 Minuten gültig.",
        invalidEmail: "Bitte gib eine gültige E-Mail-Adresse ein.",
        genericError: "Etwas ist schiefgelaufen. Bitte versuche es später erneut.",
      },
      set: {
        title: "Neues Passwort festlegen",
        description: "Wähle ein sicheres Passwort mit mindestens 8 Zeichen.",
        newPassword: "Neues Passwort",
        confirmPassword: "Passwort wiederholen",
        submit: "Passwort speichern",
        successTitle: "Passwort geändert",
        successMessage: "Dein Passwort wurde erfolgreich geändert. Du wirst gleich zum Login weitergeleitet.",
        passwordTooShort: "Das Passwort muss mindestens 8 Zeichen lang sein.",
        passwordMismatch: "Die Passwörter stimmen nicht überein.",
        tokenMissing: "Link ist ungültig",
        tokenMissingHelp: "Der Reset-Link ist unvollständig. Bitte fordere einen neuen an.",
        tokenInvalid: "Dieser Reset-Link ist ungültig oder bereits abgelaufen. Bitte fordere einen neuen an.",
        genericError: "Etwas ist schiefgelaufen. Bitte versuche es später erneut.",
      },
      invite: {
        title: "Willkommen bei SECSU",
        description: "Lege dein persönliches Passwort fest, um deinen Account zu aktivieren und dich anzumelden.",
        submit: "Account aktivieren",
        successTitle: "Account aktiviert",
        successMessage: "Dein Account ist einsatzbereit. Du wirst gleich zum Login weitergeleitet.",
      },
    },
    footer: {
      tagline: "stay secure",
    },
    // ── Phase 4: Biometrics ────────────────────────────────────────────────
    biometric: {
      lockedHint: 'Bitte authentifiziere dich, um fortzufahren.',
      unlockReason: 'secDASH entsperren',
      usePassword: 'Stattdessen Passwort verwenden',
      settingsLabel: 'Mit {label} entsperren',
      settingsDesc: 'Face ID oder Touch ID nutzen, um die App nach dem Hintergrundmodus zu entsperren.',
      confirmEnable: 'Bestätige, um die biometrische Entsperrung zu aktivieren',
      enabledSuccess: '{label} aktiviert.',
      disabledSuccess: 'Biometrische Entsperrung deaktiviert.',
      toggleFailed: 'Biometrie-Einstellung konnte nicht geändert werden.',
    },
    // ── WebAuthn / Passkeys ────────────────────────────────────────────────
    passkey: {
      title:           'Passkeys',
      loginBtn:        'Mit Face ID / Passkey anmelden',
      authError:       'Passkey-Authentifizierung fehlgeschlagen. Bitte nutze dein Passwort.',
      notSupported:    'Dieses Gerät oder dieser Browser unterstützt keine Passkeys.',
      noPasskeys:      'Noch keine Passkeys registriert.',
      unnamed:         'Unbenannter Passkey',
      addBtn:          'Passkey hinzufügen',
      addBtnConfirm:   'Passkey erstellen',
      addHint:         'Dein Gerät fragt nach Face ID, Touch ID oder Windows Hello.',
      registerSuccess: 'Passkey erfolgreich erstellt.',
      registerError:   'Passkey konnte nicht erstellt werden. Bitte versuche es erneut.',
      deleteConfirm:   'Passkey löschen',
      created:         'Erstellt',
      lastUsed:        'Zuletzt verwendet',
      backedUp:        'Synchronisiert',
      backedUpHint:    'Dieser Passkey wird geräteübergreifend synchronisiert (z.B. via iCloud-Schlüsselbund).',
      namePlaceholder: 'z.B. iPhone 15, MacBook Pro',
    },
  },
};
